import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import {
  IReturnedCheckDecisionDto,
  IReturnedCheckDecisionsDto,
} from '@/dtos/models';

import {
  CheckDecision,
  CheckDecisions,
  CheckProcessingType,
  CheckVoidReason,
} from '../returnedChecks.types';

import { LOCKBOXES_RETURNED_ITEMS_QUERY_KEY } from './useReturnedItems.query';

export const buildProcessingDecision = (decision: CheckDecision) => {
  if (decision.type === CheckProcessingType.VOID) {
    return { type: CheckProcessingType.VOID, reason: CheckVoidReason.NOT_RECEIVED };
  }

  return decision.paymentMethodId !== undefined
    ? { type: CheckProcessingType.REISSUE, payment_method_id: decision.paymentMethodId }
    : { type: CheckProcessingType.REISSUE };
};

export const buildPayload = (decisions: CheckDecisions): IReturnedCheckDecisionsDto => ({
  returned_checks: Object.entries(decisions).map(
    ([lockboxId, decision]): IReturnedCheckDecisionDto => ({
      lockbox_id: Number(lockboxId),
      processing_decision: buildProcessingDecision(decision),
    })
  ),
});

const useProcessReturnedChecksQuery = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (decisions: CheckDecisions) =>
      api.post(API_URL.LOCKBOXES_RETURNED_CHECKS_PROCESSING_DECISIONS, buildPayload(decisions)),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [LOCKBOXES_RETURNED_ITEMS_QUERY_KEY],
        });
      },
    }
  );

  const processReturnedChecks = useCallback(
    async (decisions: CheckDecisions) => {
      await mutation.mutateAsync(decisions);
    },
    [mutation]
  );

  return { processReturnedChecks, isLoading: mutation.isLoading };
};

export default useProcessReturnedChecksQuery;
